import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { 
  Users, Layers, FileText, Upload, Wand2, 
  ArrowRight, Lightbulb, ChevronRight, Home
} from "lucide-react";

/**
 * GuidanceModal - Shows friendly setup guidance when users access pages before completing prerequisites
 * 
 * Props:
 * - open: boolean - whether the modal is open
 * - onClose: function - called when modal closes
 * - type: string - the type of guidance needed: "no-characters", "no-shots", "no-script", "no-assets"
 * - projectId: string - the current project ID for navigation
 */
export default function GuidanceModal({ open, onClose, type, projectId }) {
  const navigate = useNavigate();

  const guidanceContent = {
    "no-characters": {
      icon: Users,
      iconColor: "text-indigo-400",
      bgColor: "bg-indigo-500/20",
      title: "Let's Get Your Cast Ready!",
      description: "Looks like you haven't added any characters to this project yet. No worries - it's easy to set up!",
      steps: [
        {
          icon: Upload,
          title: "Import Your Character Art",
          description: "Head to your project and use Smart Import to upload character images. We recommend PNG files with transparent backgrounds for the best results."
        },
        {
          icon: Users,
          title: "Characters Get Created Automatically",
          description: "When you import images, we'll create character profiles for you. You can then add more reference images, assign voice actors, and organize your cast."
        }
      ],
      primaryAction: {
        label: "Go to Project Assets",
        path: `/project/${projectId}`
      },
      tip: "Pro tip: Name your character image files clearly (like 'Sarah_front.png') and we'll use those names to organize everything."
    },
    "no-shots": {
      icon: Layers,
      iconColor: "text-amber-400",
      bgColor: "bg-amber-500/20",
      title: "Time to Create Your Shot List!",
      description: "The storyboard shows all your shots, but you haven't created any yet. Let's fix that in a couple easy steps.",
      steps: [
        {
          icon: FileText,
          title: "First, Add Your Script",
          description: "Go to your project and paste your screenplay into the Script tab. Don't worry about perfect formatting - just get it in there."
        },
        {
          icon: Wand2,
          title: "Then Use Script Tools",
          description: "Click 'Script Tools' and hit 'Create Shot List'. We'll analyze your script and break it into individual shots that you can animate."
        }
      ],
      primaryAction: {
        label: "Go to Script Tools",
        path: `/project/${projectId}/script-tools`
      },
      tip: "Each shot becomes a scene you'll animate. The more detail in your script, the better we can break it down for you."
    },
    "no-script": {
      icon: FileText,
      iconColor: "text-green-400",
      bgColor: "bg-green-500/20",
      title: "Add Your Script First!",
      description: "Before we can help you with script tools, we need a script to work with. It's the foundation of your project.",
      steps: [
        {
          icon: FileText,
          title: "Paste or Type Your Script",
          description: "Head to your project and click the Script tab. You can paste from your screenwriting software or type directly."
        },
        {
          icon: Lightbulb,
          title: "Standard Format Works Best",
          description: "Scene headings (INT./EXT.), character names in CAPS, and dialogue on new lines. But don't stress - we're pretty good at figuring it out."
        }
      ],
      primaryAction: {
        label: "Go to Project",
        path: `/project/${projectId}`
      },
      tip: "Even a rough draft works! You can always refine your script later and regenerate your shot list."
    },
    "no-assets": {
      icon: Upload,
      iconColor: "text-purple-400",
      bgColor: "bg-purple-500/20",
      title: "Let's Add Some Assets!",
      description: "Your project needs some visual assets before you can start animating. Here's what to do:",
      steps: [
        {
          icon: Upload,
          title: "Upload Your Artwork",
          description: "Use Smart Import to upload backgrounds (Sets), character images, and any props (Items) you'll need. Drag and drop whole folders to speed things up!"
        },
        {
          icon: Users,
          title: "Set Up Characters",
          description: "After importing character art, you can organize them into character profiles with multiple reference images for better animation results."
        }
      ],
      primaryAction: {
        label: "Go to Project",
        path: `/project/${projectId}`
      },
      tip: "Start with 2-3 characters and a couple backgrounds. You can always add more as you build out your project."
    }
  };

  const content = guidanceContent[type] || guidanceContent["no-assets"];
  const IconComponent = content.icon;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg bg-zinc-900 border-zinc-800">
        <DialogHeader>
          <div className="flex items-center gap-4 mb-2">
            <div className={`w-12 h-12 rounded-xl ${content.bgColor} flex items-center justify-center`}>
              <IconComponent className={`w-6 h-6 ${content.iconColor}`} />
            </div>
            <DialogTitle className="text-xl text-white">{content.title}</DialogTitle>
          </div>
          <p className="text-zinc-400 text-sm leading-relaxed mt-2">
            {content.description}
          </p>
        </DialogHeader>

        <div className="space-y-4 my-4">
          {content.steps.map((step, index) => (
            <div key={index} className="flex gap-4 p-4 bg-zinc-800/50 rounded-lg">
              <div className="w-10 h-10 rounded-lg bg-zinc-700/50 flex items-center justify-center shrink-0">
                <step.icon className="w-5 h-5 text-zinc-300" />
              </div>
              <div>
                <h4 className="font-medium text-white mb-1">{step.title}</h4>
                <p className="text-sm text-zinc-400">{step.description}</p>
              </div>
            </div>
          ))}
        </div>

        {content.tip && (
          <div className="flex gap-3 p-3 bg-indigo-500/10 border border-indigo-500/20 rounded-lg">
            <Lightbulb className="w-5 h-5 text-indigo-400 shrink-0 mt-0.5" />
            <p className="text-sm text-indigo-200">{content.tip}</p>
          </div>
        )}

        <DialogFooter className="mt-6 flex gap-3">
          <Button
            variant="outline"
            onClick={() => {
              onClose();
              navigate("/");
            }}
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            <Home className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
          <Button
            onClick={() => {
              onClose();
              navigate(content.primaryAction.path);
            }}
            className="bg-indigo-500 hover:bg-indigo-600 text-white gap-2"
          >
            {content.primaryAction.label}
            <ArrowRight className="w-4 h-4" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
